import { useContext } from "react";
import { useLocale } from "react-aria";
import type { DatePickerProps, DateValue } from "react-aria-components";
import {
  Calendar,
  DateInput,
  DatePicker,
  DatePickerStateContext,
  Label,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import {
  CalendarDateSegment,
  CalendarDialog,
  CalendarFieldGroup,
  CalendarI18nProvider,
  CalendarMonth,
  CalendarPopover,
  CalendarTriggerButton,
} from "./calendar-parts";
import { formatPickedDate } from "./date-picker-format";
import type { SlotClassNames } from "./types";

const datePicker = tv({
  slots: {
    base: "group flex flex-col gap-1",
    label: "text-text-secondary block text-sm font-medium",
  },
});

interface CustomDatePickerProps extends Omit<
  DatePickerProps<DateValue>,
  "children"
> {
  readonly label?: string;
  /** shown in place of the empty day/month/year segments until focused */
  readonly placeholder?: string;
  readonly className?: string;
  readonly classNames?: SlotClassNames<ReturnType<typeof datePicker>>;
}

export function CustomDatePicker({
  label,
  placeholder,
  className,
  classNames,
  value,
  onChange,
  ...props
}: Readonly<CustomDatePickerProps>) {
  const styles = datePicker();

  return (
    <CalendarI18nProvider>
      <DatePicker
        {...props}
        value={value}
        onChange={onChange}
        className={styles.base({ className: [className, classNames?.base] })}
      >
        {label && (
          <Label className={styles.label({ className: classNames?.label })}>
            {label}
          </Label>
        )}
        <CalendarFieldGroup>
          <FormattedDateInput placeholder={placeholder} />
          <CalendarTriggerButton />
        </CalendarFieldGroup>
        <CalendarPopover>
          <CalendarDialog>
            <Calendar>
              <CalendarMonth selection="single" />
            </Calendar>
          </CalendarDialog>
        </CalendarPopover>
      </DatePicker>
    </CalendarI18nProvider>
  );
}

// the segments stay mounted under the formatted label so a click lands on them
// and typing a date still works; only focus inside the segments swaps the two,
// so focus returning to the calendar button after a pick keeps the label
function FormattedDateInput({
  placeholder,
}: Readonly<{ placeholder?: string }>) {
  const { locale } = useLocale();
  // read the picker's own state so uncontrolled (defaultValue) pickers format too
  const value = useContext(DatePickerStateContext)?.value;
  const display = value ? formatPickedDate(value, locale) : placeholder;

  return (
    <>
      <DateInput
        data-display={display ? "" : undefined}
        className="peer/input flex min-w-0 flex-1 data-display:opacity-0 data-display:focus-within:opacity-100"
      >
        {(segment) => <CalendarDateSegment segment={segment} />}
      </DateInput>
      {display && (
        <span
          aria-hidden
          data-placeholder={value ? undefined : ""}
          className="data-placeholder:text-text-tertiary pointer-events-none absolute inset-y-0 right-11 left-3 flex items-center truncate peer-focus-within/input:hidden"
        >
          {display}
        </span>
      )}
    </>
  );
}
